// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Calendar from "@werner/App/Components/XngExpirationDatePicker/Calendar";
import MonthInput from "@werner/App/Components/XngExpirationDatePicker/Inputs/MonthInput";
import YearInput from "@werner/App/Components/XngExpirationDatePicker/Inputs/YearInput";
import { baseClassName } from "@werner/App/Components/XngExpirationDatePicker/utils/constants";
import {
    arrLast,
    convertStringToNumber,
    formatValue,
    getValueOrUndefined,
    getYearParts,
    getYearValue,
    monthOnSubmitValidator,
    yearOnSubmitValidator,
} from "@werner/App/Components/XngExpirationDatePicker/utils/helpers";
import { CalendarUnit, HasAvailableUnits } from "@werner/App/Components/XngExpirationDatePicker/utils/types";

import React, { FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";

export interface ExpirationDateValue<T> {
    month: T;
    year: T;
}

export interface ExpirationDatePlaceholders {
    monthPlaceholder?: string;
    yearPlaceholder?: string;
}

interface PickerInputProps extends HasAvailableUnits {
    onChange?: (value: ExpirationDateValue<number>) => void;
    value?: ExpirationDateValue<number>;
    calendarIcon?: React.ReactNode;
    placeholders?: ExpirationDatePlaceholders;
    yearFormat?: "2-digits" | "4-digits";
    disabled?: boolean;
    id?: string;
    ariaLabel?: string;
    monthAriaLabel?: string;
    yearAriaLabel?: string;
    keepCalendarOpen?: boolean;
    monthFormat?: "numbers" | "names";
    onCalendarOpen?: () => void;
}

const IconButton = styled.button<InjectableCss>`
    margin-left: 10px !impotant;
    ${injectCss}
`;

const Divider = styled.span<InjectableCss>`
    ${injectCss}
`;

const PickerInput: FC<PickerInputProps> = ({
    onChange,
    value,
    calendarIcon,
    availableMonths,
    availableYears,
    placeholders,
    yearFormat,
    disabled,
    id,
    ariaLabel,
    monthAriaLabel,
    yearAriaLabel,
    keepCalendarOpen,
    monthFormat,
    onCalendarOpen,
}) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<ExpirationDateValue<string>>({
        month: formatValue(value?.month || new Date().getMonth() + 1),
        year: getYearValue(value?.year || new Date().getFullYear(), yearFormat!),
    });
    const [calendarYear, setCalendarYear] = useState<number>(value?.year || new Date().getFullYear());

    const yearRef = useRef<HTMLInputElement>(null);
    const monthRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        document.addEventListener("focusin", handleClickAway);
        document.addEventListener("mousedown", handleClickAway);
        document.addEventListener("touchstart", handleClickAway);
        return () => {
            document.removeEventListener("focusin", handleClickAway);
            document.removeEventListener("mousedown", handleClickAway);
            document.removeEventListener("touchstart", handleClickAway);
        };
    }, []);

    React.useEffect(() => {
        setSelectedValue({
            month: formatValue(value?.month || new Date().getMonth() + 1),
            year: getYearValue(value?.year || new Date().getFullYear(), yearFormat!),
        });
    }, [value?.month, value?.year]);

    const handleSetCalendarOpen = (open: boolean) => {
        setIsCalendarOpen(open);
        open && onCalendarOpen?.();
    };

    const handleClickAway = (e: any) => {
        // the user clicked outside this component
        if (!wrapperRef.current?.contains(e.target)) {
            handleSetCalendarOpen(false);
        }
    };

    const handleChange = (value: ExpirationDateValue<number>) => {
        const lastAvailableYear = arrLast(availableYears)?.value;
        const yearValidator = yearOnSubmitValidator(value.year, lastAvailableYear);
        const monthValidator = monthOnSubmitValidator(value.month);
        if (yearValidator && monthValidator) {
            onChange?.(value);
        }
    };

    const handleSelectMonth = (month: CalendarUnit, selectedYear: number) => {
        const value = {
            month: `${month.value}`,
            year: getYearValue(selectedYear, yearFormat!),
        };

        setSelectedValue(value);

        handleSetCalendarOpen(false);

        handleChange({
            month: month.value,
            year: selectedYear,
        });
    };

    const handleMonthChange = (month: string) => {
        setSelectedValue({
            ...selectedValue,
            month,
        });

        handleChange({
            month: convertStringToNumber(month),
            year: calendarYear,
        });

        if (month.length >= 2) {
            yearRef.current?.focus();
        } else {
            monthRef.current?.focus();
        }
    };

    const handleYearChange = (year: string) => {
        const { century } = getYearParts(convertStringToNumber(year));
        const calendarYear =
            yearFormat === "2-digits" ? convertStringToNumber(`${century}${year}`) : convertStringToNumber(year);

        setSelectedValue({
            ...selectedValue,
            year,
        });
        if (calendarYear.toString().length === 4) {
            setCalendarYear(calendarYear);
        }
        handleChange({
            month: convertStringToNumber(selectedValue.month),
            year: calendarYear,
        });

        const yearLength = yearFormat === "4-digits" ? 4 : 2;
        if (year.length >= yearLength) {
            yearRef.current?.blur();
            handleSetCalendarOpen(false);
        }
    };

    const handleInputFocus = () => {
        handleSetCalendarOpen(true);
    };

    const handleYearInputBlur = () => {
        if (selectedValue.year === "") {
            handleYearChange(yearFormat === "2-digits" ? `${calendarYear}`.slice(-2) : `${calendarYear}`);
        }
    };

    const calendarState = keepCalendarOpen || isCalendarOpen ? "open" : "closed";
    const selectedMonth = getValueOrUndefined(selectedValue.month);
    const statusClassName = disabled ? `${baseClassName}--disabled` : `${baseClassName}--enabled`;
    const lastAvailableYear = arrLast(availableYears)?.value;
    return (
        <StyledWrapper
            id={id}
            className={`${baseClassName} ${baseClassName}--${calendarState} ${statusClassName}`}
            ref={wrapperRef}
        >
            <StyledWrapper className={`${baseClassName}__wrapper`}>
                <StyledWrapper className={`${baseClassName}__inputGroup`} aria-label={ariaLabel}>
                    <MonthInput
                        name="month"
                        placeholder={placeholders?.monthPlaceholder}
                        onFocus={handleInputFocus}
                        inputRef={monthRef}
                        onChange={handleMonthChange}
                        value={selectedValue.month}
                        disabled={disabled}
                        ariaLabel={monthAriaLabel}
                    />
                    <Divider className={`${baseClassName}__inputGroup__divider`}>/</Divider>
                    <YearInput
                        name="year"
                        placeholder={placeholders?.yearPlaceholder}
                        onFocus={handleInputFocus}
                        inputRef={yearRef}
                        onChange={handleYearChange}
                        value={selectedValue.year}
                        yearFormat={yearFormat}
                        disabled={disabled}
                        lastAvailableYear={lastAvailableYear}
                        ariaLabel={yearAriaLabel}
                        onBlur={handleYearInputBlur}
                    />
                </StyledWrapper>
                {calendarIcon && (
                    <IconButton
                        className={`${baseClassName}__calendar-button ${baseClassName}__button`}
                        onClick={() => handleSetCalendarOpen(!isCalendarOpen)}
                        disabled={disabled}
                        type="button"
                        aria-label="Toggle Calendar Button"
                    >
                        {calendarIcon}
                    </IconButton>
                )}
            </StyledWrapper>
            <Calendar
                calendarState={calendarState}
                selectedMonth={selectedMonth}
                selectedYear={calendarYear}
                onMonthClick={handleSelectMonth}
                availableMonths={availableMonths}
                availableYears={availableYears}
                monthFormat={monthFormat}
            />
        </StyledWrapper>
    );
};

PickerInput.defaultProps = {
    placeholders: {
        monthPlaceholder: "mm",
        yearPlaceholder: "yy",
    },
    monthAriaLabel: "month input",
    yearAriaLabel: "year input",
    yearFormat: "2-digits",
    monthFormat: "numbers",
};

export default PickerInput;
